import { useCallback, useContext, useMemo } from 'react';
import FolderOutlinedIcon from '@mui/icons-material//FolderOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material//KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material//KeyboardArrowUp';
import { IconButton, styled, TableCell, TableRow, Typography } from '@mui/material';
import { FolderView } from 'api';
import { DialogContextValues } from 'common/types/dialogContext';
import { ConfirmationDelete, ConfirmationDialog } from 'components/Dialogs';
import ExtraActionsMenu from 'components/ExtraActionsMenu';
import { DialogContext } from 'context';
import { InformationDashboard409AContext } from 'context/InformationDashboard409AContext';
import { InformationContextValue } from 'context/InformationDashboard409AContext/types';
import {
  DELETE_FOLDER_LABEL,
  DELETE_FOLDER_MESSAGE,
  DELETE_FOLDER_TITLE,
  EDIT_FOLDER_LABEL,
  EMPTY_FOLDER_MESSAGE,
} from 'dashboard409a/common/constants/layouts/uploadedDocumentsTable';
import { useDocuments } from 'services/hooks';
import theme from 'theme';
import { CurrentRequestedDocumentTableFolderRowProps, ExtendedFolderView } from './types';
import { useDropRow } from '../hooks/useDragAndDropRow';

const ActionMenuContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
});

const HiddenTableRow = styled(TableRow)({
  display: 'none',
});

const FolderContainer = styled('div')<{ isSubFolder: boolean }>(({ theme, isSubFolder }) => ({
  color: theme?.palette?.primary?.main,
  fontWeight: 'bold',
  display: 'flex',
  alignItems: 'center',
  paddingLeft: isSubFolder ? '1rem' : '0',
}));

const FolderText = styled(Typography)(({ theme }) => ({
  color: theme?.palette?.primary?.main,
  fontWeight: 'bold',
}));

const FolderIcon = styled(FolderOutlinedIcon)(({ theme }) => ({
  marginRight: theme?.spacing(1),
}));

const ClickableTableCell = styled(TableCell)({
  cursor: 'pointer',
});

const StyledArrowIcon = styled('div')(({ theme }) => ({
  marginRight: theme.spacing(1),
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  '&.drag-over': {
    outline: `1.5px dashed ${theme.palette.primary.main}`,
  },
}));

const StyledTableCell = styled(TableCell)({
  fontSize: theme.spacing(2),
});

const CurrentRequestedDocumentTableFolderRow = ({
  folder,
  refreshDocuments,
  addFilesToFolder,
  draggedRow,
  draggedSelectedFiles,
  onDropSuccess,
  toggleFolder,
  isFolderOpen,
  showFolder,
  setSelectedFolder,
  currentDocumentsId,
  folderStatusMap,
}: CurrentRequestedDocumentTableFolderRowProps) => {
  const isSubFolder = !!folder.parent_folder;
  const folderId = folder.id ?? 0;

  const { deleteFolder } = useDocuments();
  const { showDialog } = useContext(DialogContext) as unknown as DialogContextValues;
  const { handleDragOver, handleDrop, handleDragLeave } = useDropRow({
    draggedRow,
    draggedSelectedFiles,
    addFilesToFolder,
    refreshDocuments,
    onDropSuccess,
  });
  const { permissions } = useContext(InformationDashboard409AContext) as unknown as InformationContextValue;

  const folderName = useMemo(() => folder.name.toUpperCase(), [folder]);

  const deleteFolderCallback = useCallback(async () => {
    if (currentDocumentsId) {
      await deleteFolder(currentDocumentsId, folderId, folderName);
      refreshDocuments();
    }
  }, [deleteFolder, currentDocumentsId, folderId, folderName, refreshDocuments]);

  const handleDeleteFolder = useCallback(() => {
    showDialog({
      actions: [
        {
          label: DELETE_FOLDER_LABEL,
          variant: 'contained',
          type: 'danger',
          callback: deleteFolderCallback,
        },
      ],
      content: ConfirmationDelete,
      contentProps: { customMessage: DELETE_FOLDER_MESSAGE },
      wrapper: ConfirmationDialog,
      wrapperProps: {
        title: DELETE_FOLDER_TITLE,
      },
    });
  }, [deleteFolderCallback, showDialog]);

  const fileOptionsForExtraActionsMenu = useCallback(
    (row: FolderView) => [
      {
        label: EDIT_FOLDER_LABEL,
        callback: () => setSelectedFolder(row),
        isActive: true,
      },
      {
        label: DELETE_FOLDER_LABEL,
        callback: handleDeleteFolder,
        isActive: true,
      },
    ],
    [handleDeleteFolder, setSelectedFolder]
  );

  const isEmptyFolder = useMemo(
    () => !(folder as ExtendedFolderView).files?.length && !(folder as ExtendedFolderView).childFolders?.length,
    [folder]
  );

  const handleToggleFolder = (targetFolderId: number) => {
    const childFolderIds = Object.keys(folderStatusMap)
      .filter(key => folderStatusMap[Number(key)].parent === targetFolderId)
      .map(key => Number(key));
    toggleFolder(targetFolderId, childFolderIds);
  };

  const TableRowComponent = showFolder ? StyledTableRow : HiddenTableRow;

  return (
    <>
      <TableRowComponent
        onDrop={event => handleDrop(event, folderId)}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}>
        <ClickableTableCell colSpan={6} onClick={() => handleToggleFolder(folderId)}>
          <FolderContainer isSubFolder={isSubFolder}>
            <FolderIcon />
            <FolderText variant="subtitle1">{folderName}</FolderText>
          </FolderContainer>
        </ClickableTableCell>
        <StyledTableCell>
          <ActionMenuContainer>
            <IconButton size="small" onClick={() => handleToggleFolder(folderId)}>
              <StyledArrowIcon>{isFolderOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</StyledArrowIcon>
            </IconButton>
            {permissions?.canUseInfoRequestLedger && (
              <ExtraActionsMenu options={fileOptionsForExtraActionsMenu(folder)} dotsColor={theme.palette.grey[500]} />
            )}
          </ActionMenuContainer>
        </StyledTableCell>
      </TableRowComponent>
      {isFolderOpen && isEmptyFolder && (
        <TableRow>
          <StyledTableCell colSpan={7}>
            <Typography variant="body2" color="textSecondary" align="center" style={{ paddingLeft: '2rem' }}>
              {EMPTY_FOLDER_MESSAGE}
            </Typography>
          </StyledTableCell>
        </TableRow>
      )}
    </>
  );
};

export default CurrentRequestedDocumentTableFolderRow;
